<template>
  <div class="nav-bar">
    <div class="top-bar">
      <div class="customer-info">
        <div class="customer-name">היי, {{ this.householdName }}</div>
        <div class="subscription-state" :class="subscriptionStateVariant" @click="subscriptionStateClicked" v-if="subscriptionStateText">
          {{ subscriptionStateText }}
        </div>
        <div class="last-updated">עדכון אחרון:  {{ lastUpdated }}</div>
      </div>
      <div class="menu">
        <navigation-menu-item title="תזרים חודשי" v-on:navigate="closeModal" target-path="sr"/>
        <balance-page-menu-item v-on:navigate="closeModal"/>
        <saving-zone-menu-item v-on:navigate="closeModal"/>
        <balance-money-menu-item v-on:navigate="closeModal" target-path="balance-money"/>
        <plan-ahead-menu-item v-on:navigate="goToPlanAheadPage"/>
        <navigation-menu-item title="כמה עלה לי?" v-on:navigate="closeModal" target-path="stories"/>
      </div>
    </div>
    <div class="bottom-bar">
      <navigation-button @navigate-clicked="openSettings" button-text="הגדרות" image-path="nav/settings.svg"></navigation-button>
      <navigation-button @navigate-clicked="goToAmbassadors" :button-text="referralButtonText()"
                           image-path="nav/ambassadors.svg" lottie-path="animations/Starling.json" :show-expand="false"/>
      <navigation-button @navigate-clicked="openChat" button-text="צ׳אט עם הצוות" image-path="nav/chat.svg" :show-expand="false"/>
      <navigation-button @navigate-clicked="goToFacebook" button-text="הקהילה שלנו בפייסבוק" image-path="nav/fb.svg" :show-expand="false"/>
      <savings-navigation-button @navigate-clicked="goToSavingProduct"/>
      <navigation-button @navigate-clicked="openFaq" button-text="מרכז המידע" image-path="nav/help.svg" :show-expand="false"/>
      <navigation-button @navigate-clicked="redirectToLogin" button-text="יציאה" image-path="nav/exit.svg" :show-expand="false"/>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import moment from 'moment';
import { SUBSCRIPTION_STATES } from '@/constants/subscription-state';
import BalanceMoneyMenuItem from '@/base-components/app-header/BalanceMoneyMenuItem';
import SavingsNavigationButton from './SavingsNavigationButton';
import router from '../../router';
import NavigationButton from './NavigationButton.vue';
import Intercom from '../../Intercom';
import trialUtils from '../../pages/responsive-pages/authenticated/trial/trial-utils';
import Segment from '../../Segment';
import NavigationMenuItem from './NavigationMenuItem.vue';
import BalancePageMenuItem from './BalancePageMenuItem.vue';
import SavingZoneMenuItem from './SavingZoneMenuItem';
import PlanAheadMenuItem from './PlanAheadMenuItem';

export default {
  name: 'NavigationMenu',
  components: {
    SavingsNavigationButton,
    SavingZoneMenuItem,
    BalanceMoneyMenuItem,
    BalancePageMenuItem,
    NavigationMenuItem,
    NavigationButton,
    PlanAheadMenuItem,
  },
  data() {
    return {
      showSavingZone: false,
    };
  },
  props: {
    closeModal: {
      type: Function,
      required: true,
    },
  },
  computed: {
    ...mapGetters('session', ['householdName']),
    ...mapGetters('cashflowView', ['budgetUpdatedAt']),
    ...mapState('subscriptionState', ['subscriptionState']),
    ...mapGetters('subscriptionState', ['daysLeft', 'trialPeriod', 'minDaysToPresentTrialNotifications']),
    ...mapGetters('featureFlags', ['keepAmbassadorsInApp', 'referralsAbcTest']),
    ...mapState('session', ['inviteToken']),
    subscriptionStateText() {
      switch (this.subscriptionState) {
      case SUBSCRIPTION_STATES.PRE_TRIAL: return '';
      case SUBSCRIPTION_STATES.TRIAL: return this.trialCounterText;
      case SUBSCRIPTION_STATES.DORMANT_AFTER_CANCELED: return 'המנוי לא פעיל';
      case SUBSCRIPTION_STATES.DORMANT_AFTER_TRIAL: return 'המנוי לא פעיל';
      case SUBSCRIPTION_STATES.FREE: return '';
      case SUBSCRIPTION_STATES.SUBSCRIBED: return '';
      case SUBSCRIPTION_STATES.CANCELED: return 'חיוב אחרון נכשל';
      default: return '';
      }
    },
    lastUpdated() {
      const updateHour = moment(this.budgetUpdatedAt).format('H:mm');
      const updateDate = moment(this.budgetUpdatedAt).format('D.MM');
      return `${updateDate} ${updateHour} `;
    },
    subscriptionStateVariant() {
      switch (this.subscriptionState) {
      case SUBSCRIPTION_STATES.PRE_TRIAL: return 'warning';
      case SUBSCRIPTION_STATES.TRIAL: return trialUtils.periodVariant(this.trialPeriod);
      case SUBSCRIPTION_STATES.DORMANT_AFTER_CANCELED: return 'danger';
      case SUBSCRIPTION_STATES.DORMANT_AFTER_TRIAL: return 'danger';
      case SUBSCRIPTION_STATES.FREE: return 'warning';
      case SUBSCRIPTION_STATES.SUBSCRIBED: return 'warning';
      case SUBSCRIPTION_STATES.CANCELED: return 'danger';
      default: return 'warning';
      }
    },
    trialCounterText() {
      return `${trialUtils.humanizeDays(this.daysLeft)} לתקופת הניסיון`;
    },
  },
  methods: {
    ...mapActions('login', ['logout']),
    ...mapActions('modalRootStore', ['openModal']),
    referralButtonText() {
      Segment.trackUserGot('NavigationMenu_GotReferralText', { referralButtonText: this.referralsAbcTest });
      if (this.referralsAbcTest === 'recAndEarn') {
        return 'להמליץ ולהרוויח';
      }
      if (this.referralsAbcTest === 'prize') {
        return 'להזמין אחרים לצמוח';
      }
      return 'לפרגן לחבר.ה בחודשיים מתנה';
    },
    subscriptionStateClicked() {
      Segment.trackUserInteraction('NavigationMenu_SubscriptionStatusClicked', { trialPeriod: this.trialPeriod });
      this.navigate('AccountSettings');
      this.closeModal();
    },
    openSettings() {
      Segment.trackUserInteraction('NavigationMenu_SettingsClicked');
      this.navigate('Settings');
      this.closeModal();
    },
    openFaq() {
      Segment.trackUserInteraction('NavigationMenu_HelpCenterClicked');
      window.open('https://intercom.riseup.co.il/he', '_blank');
      this.closeModal();
    },
    openSummerSite() {
      Segment.trackUserInteraction('NavigationMenu_SummerSiteClicked');
      window.open('https://summer.riseup.co.il', '_blank');
      this.closeModal();
    },
    async redirectToLogin() {
      await this.logout();
      this.closeModal();
    },
    goToFacebook() {
      Segment.trackUserInteraction('NavigationMenu_FacebookGroup');
      window.open('https://www.facebook.com/groups/riseuptogethergroup', '_blank');
      this.closeModal();
    },
    goToAmbassadors() {
      Segment.trackUserInteraction('NavigationMenu_AmbassadorsClicked');
      if (this.keepAmbassadorsInApp) {
        router.push({ path: '/ambassadors' });
      } else {
        window.open('https://www.riseup.co.il/ambassadors', '_blank');
      }
      this.closeModal();
    },
    goToSavingProduct() {
      Segment.trackUserInteraction('NavigationMenu_GoToSavingProduct');
      window.open('https://www.riseup.co.il/save', '_blank');
      this.closeModal();
    },
    openChat() {
      Segment.trackUserInteraction('NavigationMenu_ChatWithTeamClicked');
      Intercom.show();
      this.closeModal();
    },
    goToPlanAheadPage() {
      Segment.trackUserInteraction('NavigationMenu_PlanAheadDashboardClicked');
      this.closeModal();
    },
    navigate(name) {
      router.push({ name, query: this.$route.query });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../scss/mixins';

  .nav-bar {
    min-height: 100%;
    background: $riseup_white;
    box-shadow: 0px 0 15px 0 $riseup_gray_2;

    .top-bar {
      .customer-info {
        padding: 40px 24px;
        border-bottom: 1px solid $riseup_gray_0;

        .customer-name {
          font-family: SimplerProBold;
          font-size: 24px;
          font-weight: bold;
          color: $riseup_black;
        }
        .subscription-state {
          margin-top: 12px;
          font-size: 20px;
          text-decoration: underline;
          cursor: pointer;

          &.warning {
            color: $riseup_yellow;
          }

          &.danger {
            color: $riseup_danger_red;
          }
        }

        .last-updated {
          margin-top: 12px;
          font-family: SimplerPro;
          font-size: 14px;
          color: $riseup_gray_3;
        }
      }

      .menu {
        padding: 30px 24px 0 30px;
        border-bottom: 1px solid $riseup_gray_0;

        .navigation-link {
          display: flex;
          align-items: center;
          margin-bottom: 30px;
          font-size: 20px;
          cursor: pointer;

          .icon {
            margin-right: 10px;
          }
        }
      }
    }

    .bottom-bar {
      padding: 0 24px;
    }
  }
</style>
